/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { getEnvironment } from 'src/core/utils/api/environment';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AppSwagger } from 'src/luis/projects/apps/types/swagger/AppSwagger';
import { AppsToEmailSwagger, AppsToExportSwagger } from 'src/verticals/azureMigrationModal/services/azureMigrationSwagger';

/**
 * Represents a service for user related actions.
 */
export const getAzureMigrationService = createService(() => {
    const getUrl = (regionWebApiUrl: string, resource: string = undefined): string => {
        const builder = urlBuilder()
            .baseUrl(regionWebApiUrl)
            .resource('user');

        if (resource) {
            builder.resource(resource);
        }

        return builder.build();
    };

    const getBaseAPIOptions = (serviceKey: string, isProduction: boolean) =>
        luisRequestOptionsBuilder()
            .useAuthoringKey(serviceKey, isProduction)
            .useContentType('json');

    /**
     * Migrate the user.
     *
     * @param subscriptionId The subscription id to migrate to.
     * @param resourceGroup The resource group to migrate to.
     * @param accountName The account name to migrate to.
     * @param accountKeyString The account key string to migrate to.
     */
    const migrate = async ({
        endpoint,
        serviceKey,
        accountName,
        tenantToken,
        resourceGroup,
        azureSubscriptionId
    }: {
        endpoint: string;
        tenantToken: string;
        azureSubscriptionId: string;
        resourceGroup: string;
        accountName: string;
        serviceKey: string;
    }): Promise<void> => {
        const { webApiUrl, isProduction } = getEnvironment({ currentResourceEndpoint: endpoint });

        const options = getBaseAPIOptions(serviceKey, isProduction)
            .useArmToken(tenantToken)
            .build();

        await httpService.put(getUrl(webApiUrl, 'authoringAzureAccount'), { azureSubscriptionId, resourceGroup, accountName }, options);
    };

    /**
     * Gets the apps that the current user owns and
     * has collaborators for a given public authoring endpoint.
     */
    const getAppsToEmail = async ({
        endpoint,
        serviceKey,
        tenantToken
    }: {
        endpoint: string;
        serviceKey: string;
        tenantToken: string;
    }): Promise<AppsToEmailSwagger[]> => {
        const { webApiUrl, isProduction } = getEnvironment({ currentResourceEndpoint: endpoint });

        const options = getBaseAPIOptions(serviceKey, isProduction)
            .useArmToken(tenantToken)
            .build();

        const { data: appsWithCollaborators } = await httpService.get<{ applications: AppsToEmailSwagger[] }>(
            getUrl(webApiUrl, 'collaborators'),
            options
        );

        return appsWithCollaborators?.applications;
    };

    /**
     * Gets the apps that the current user is a collaborator on
     * along with these apps' owners for a given public authoring endpoint.
     */
    const getAppsToExport = async ({ serviceKey, endpoint }: { serviceKey: string; endpoint: string }): Promise<AppsToExportSwagger[]> => {
        const { webApiUrl, isProduction } = getEnvironment({ currentResourceEndpoint: endpoint });

        const apiOptions = getBaseAPIOptions(serviceKey, isProduction).build();

        const { data: appsWithOwners } = await httpService.get<{ applications: AppsToExportSwagger[] }>(
            getUrl(webApiUrl, 'unownedAppsOwners'),
            apiOptions
        );

        return appsWithOwners?.applications;
    };

    /**
     * Exports the app with the given id in a JSON formate.
     *
     * @param param0.fileName The exported file name to be.
     */
    const exportApp = async ({
        appId,
        endpoint,
        fileName,
        serviceKey
    }: {
        appId: string;
        fileName: string;
        serviceKey: string;
        endpoint: string;
    }): Promise<void> => {
        const { apiUrl, isProduction } = getEnvironment({ currentResourceEndpoint: endpoint, apiVersion: 'v3.0-preview' });

        const getAppUrl = urlBuilder()
            .baseUrl(apiUrl)
            .app(appId)
            .build();

        const getAppOptions = getBaseAPIOptions(serviceKey, isProduction).build();

        const { data: newAppItem } = await httpService.get<AppSwagger>(getAppUrl, getAppOptions);

        const exportUrl = urlBuilder()
            .baseUrl(apiUrl)
            .app(appId)
            .version(newAppItem.activeVersion)
            .resource('export')
            .queryParam('format', 'json')
            .build();

        const exportOptions = getBaseAPIOptions(serviceKey, isProduction).build();

        await fileService.download(exportUrl, `${fileName}.json`, exportOptions);
    };

    /**
     * Gets a list of apps belongs for a given
     * public authoring endpoint. and service key.
     */
    const getConversationAppsPerRegion = async ({
        serviceKey,
        endpoint
    }: {
        serviceKey: string;
        endpoint: string;
    }): Promise<AppSwagger[]> => {
        const { apiUrl, isProduction } = getEnvironment({ apiVersion: 'v3.0-preview', currentResourceEndpoint: endpoint });

        const customApiOptions = getBaseAPIOptions(serviceKey, isProduction).build();

        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app()
            .queryParam('take', `${LuisConstants.ResourceLimits.ServiceFetchSizeLimit}`)
            .build();

        const { data: appItems } = await httpService.get<AppSwagger[]>(url, customApiOptions);

        return appItems;
    };

    /**
     * Deletes the user belongs to the given service key.
     */
    const deleteUserInRegion = async ({ serviceKey, endpoint }: { serviceKey: string; endpoint: string }) => {
        const { webApiUrl, isProduction } = getEnvironment({ apiVersion: 'v2.0', currentResourceEndpoint: endpoint });

        const deleteUserUrl = urlBuilder()
            .baseUrl(webApiUrl)
            .resource('user')
            .build();

        const deleteUserOptions = getBaseAPIOptions(serviceKey, isProduction).build();

        await httpService.delete(deleteUserUrl, deleteUserOptions);
    };

    return { migrate, getAppsToEmail, getAppsToExport, exportApp, getConversationAppsPerRegion, deleteUserInRegion };
});
